import clipboardCopyIcon from "@tabler/icons/outline/clipboard-copy.svg";
import dotsIcon from "@tabler/icons/outline/dots.svg";
import flagIcon from "@tabler/icons/outline/flag.svg";
import logoutIcon from "@tabler/icons/outline/logout.svg";
import shareIcon from "@tabler/icons/outline/share.svg";
import volume3Icon from "@tabler/icons/outline/volume-3.svg";
import React, { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useLeaveGroup, useMuteGroup, useUnmuteGroup } from "@/api/groups";
import DropdownMenu, { type Menu } from "@/components/dropdown-menu";
import { IconButton } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { ReportableEntities } from "@/contexts/reports-context";
import { useLoggedIn } from "@/hooks";
import { GroupRoles } from "@/schemas/group-member";
import toast from "@/toast";
import copy from "@/utils/copy";

import type { Group } from "@/schemas";

const messages = defineMessages({
  confirmationConfirm: {
    id: "confirmations.leave_group.confirm",
    defaultMessage: "Leave",
  },
  confirmationHeading: {
    id: "confirmations.leave_group.heading",
    defaultMessage: "Leave group",
  },
  confirmationMessage: {
    id: "confirmations.leave_group.message",
    defaultMessage:
      "You are about to leave the group. Do you want to continue?",
  },
  copy: { id: "group.copy", defaultMessage: "Copy link to group" },
  copied: { id: "copy.success", defaultMessage: "Copied to clipboard!" },
  muteConfirm: {
    id: "confirmations.mute_group.confirm",
    defaultMessage: "Mute",
  },
  muteSubscribedConfirm: {
    id: "confirmations.mute_group.subscribed_confirm",
    defaultMessage: "Ok",
  },
  muteHeading: {
    id: "confirmations.mute_group.heading",
    defaultMessage: "Mute Group",
  },
  muteSubscribedHeading: {
    id: "confirmations.mute_group.subscribed_heading",
    defaultMessage: "Mute Subscribed Group",
  },
  muteMessage: {
    id: "confirmations.mute_group.message",
    defaultMessage: "You are about to mute the group. Do you want to continue?",
  },
  muteSubscribedMessage: {
    id: "confirmations.mute_group.subscribed_message",
    defaultMessage:
      "Muting a group that you are subscribed to will unsubscribe you from the group.",
  },
  leave: { id: "group.leave.label", defaultMessage: "Leave" },
  leaveSuccess: { id: "group.leave.success", defaultMessage: "Left the group" },
  mute: { id: "group.mute.label", defaultMessage: "Mute" },
  unmute: { id: "group.unmute.label", defaultMessage: "Unmute" },
  report: { id: "group.report.label", defaultMessage: "Report" },
  share: { id: "group.share.label", defaultMessage: "Share" },
});

interface GroupActionButtonProps {
  group: Group;
}

const GroupOptionsButton = ({ group }: GroupActionButtonProps) => {
  const { currentAccount } = useAuth();
  const intl = useIntl();
  const { isLoggedIn } = useLoggedIn();
  const { openModal } = useModalContext();

  const muteGroup = useMuteGroup();
  const unmuteGroup = useUnmuteGroup();
  const leaveGroup = useLeaveGroup();

  const isMember = group.relationship?.role === GroupRoles.USER;
  const isAdmin = group.relationship?.role === GroupRoles.ADMIN;
  const isBlocked = group.relationship?.blocked_by;
  const isMuting = group.relationship?.muting;
  const isSubscribed = group.relationship?.notifying;

  const handleShare = () => {
    navigator
      .share({
        text: group.display_name,
        url: group.url,
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e);
      });
  };

  const handleMute = () => {
    openModal("CONFIRM", {
      heading: intl.formatMessage(
        isSubscribed ? messages.muteSubscribedHeading : messages.muteHeading,
      ),
      message: intl.formatMessage(
        isSubscribed ? messages.muteSubscribedMessage : messages.muteMessage,
      ),
      confirm: intl.formatMessage(
        isSubscribed ? messages.muteSubscribedConfirm : messages.muteConfirm,
      ),
      confirmationTheme: "default",
      onConfirm: () => {
        muteGroup.mutate({
          id: group.relationship!.id,
          isSubscribed: isSubscribed || undefined,
        });
      },
    });
  };

  const handleUnmute = () => {
    unmuteGroup.mutate({ id: group.relationship!.id });
  };

  const handleCopy = () => {
    copy(group.url, () => {
      toast.success(intl.formatMessage(messages.copied));
    });
  };

  const handleLeave = () =>
    openModal("CONFIRM", {
      heading: intl.formatMessage(messages.confirmationHeading),
      message: intl.formatMessage(messages.confirmationMessage),
      confirm: intl.formatMessage(messages.confirmationConfirm),
      onConfirm: () =>
        leaveGroup.mutate(
          { id: group.relationship!.id },
          {
            onSuccess() {
              toast.success(intl.formatMessage(messages.leaveSuccess));
            },
          },
        ),
    });

  const menu: Menu = useMemo(() => {
    const canShare = "share" in navigator;
    const items = [];

    if (canShare) {
      items.push({
        text: intl.formatMessage(messages.share),
        icon: shareIcon,
        action: handleShare,
      });
    }

    items.push({
      text: intl.formatMessage(messages.copy),
      action: handleCopy,
      icon: clipboardCopyIcon,
    });

    if (isLoggedIn) {
      items.push({
        text: isMuting
          ? intl.formatMessage(messages.unmute)
          : intl.formatMessage(messages.mute),
        icon: volume3Icon,
        action: isMuting ? handleUnmute : handleMute,
        testId: "dropdown-item-mute",
      });

      if (isMember || isAdmin) {
        items.push({
          text: intl.formatMessage(messages.report),
          icon: flagIcon,
          action: () => {
            openModal("REPORT", {
              entityType: ReportableEntities.GROUP,
              account: currentAccount!,
              group,
            });
          },
        });
      }

      if (isAdmin) {
        items.push(null);
        items.push({
          text: intl.formatMessage(messages.leave),
          icon: logoutIcon,
          action: handleLeave,
        });
      }
    }

    return items;
  }, [isMember, isAdmin, isMuting, isSubscribed]);

  if (isBlocked || menu.length === 0) {
    return null;
  }

  return (
    <DropdownMenu items={menu} placement="bottom">
      <IconButton
        src={dotsIcon}
        variant="outline"
        iconClassName="h-5 w-5"
        className="px-2.5"
        data-testid="dropdown-menu-button"
      />
    </DropdownMenu>
  );
};

export default GroupOptionsButton;
